import { Component, EventEmitter, Inject, Input, Output } from "@angular/core";

import { AircraftService } from "../aircraft.service";
import { DealerAircraftService } from "../../dealer-customer-management/dealer-aircraft.service";
import { Device } from "../device";
import { DeviceModel } from "../device-model";
import { Observable } from 'rxjs';
import { Aircraft } from '../aircraft';

enum State {
	Loading,
	SynchronizeTerrain,
	MakeLsqCapableSelection,
	ActivateCCD
}

@Component({
	selector: "activate-ccd-modal",
	template: `
		<div>
			<loading-modal *ngIf="currentState==State.Loading"></loading-modal>
		</div>

		<div class="ccd-activation-modal">

			<!-- Synchronize Device Terrain dbs to 4.9as if needed -->
			<activate-ccd-synchronize-terrain-step
					(modalClosed)="onModalClosed()"
					(upgradeSuccess)="proceedToActivate()"
					[requiredUpgradeDevices]="requiredUpgradeDevices"
					[isDealerCustomerManagementFlow]="isDealerCustomerManagementFlow"
					[grantorCustomerGuid]="customerGuid"
					*ngIf="currentState==State.SynchronizeTerrain">
			</activate-ccd-synchronize-terrain-step>

			<activate-ccd-lsq-capable-selection-step
					(modalClosed)="onModalClosed()"
					[deviceModelName]="ccDeviceModel.name"
					*ngIf="currentState==State.MakeLsqCapableSelection"
					(aircraftLsqCapableSelectionMade)="onAircraftLsqCapableSelectionMade($event)">
			</activate-ccd-lsq-capable-selection-step>

			<activate-ccd-promote-register-step
					(ccdRegistered)="onCcdRegistered($event)"
					(modalClosed)="onModalClosed()"
					[aircraftDevices]="aircraftDevices"
					[ccDeviceModel]="ccDeviceModel"
					[customerGuid]="customerGuid"
					[isDealerCustomerManagementFlow]="isDealerCustomerManagementFlow"
					[isAircraftLsqCapable]="isAircraftLsqCapable"
					[aircraftTailNumber]="aircraftTailNumber"
					[customerAircraftUuid]="customerAircraftUuid"
					[masterAircraftUuid]="masterAircraftUuid"
					*ngIf="currentState==State.ActivateCCD">
			</activate-ccd-promote-register-step>
		</div>
	`
})
export class ActivateCCDModalComponent {
	@Input() ccDeviceModel: DeviceModel;
	@Input() customerGuid: string;
	@Input() isDealerCustomerManagementFlow: boolean;
	@Input() aircraftTailNumber: string;
	@Input() aircraftUuid: string;
	@Input() customerAircraftUuid: string;
	@Input() masterAircraftUuid: string;
	@Output() modalClosed = new EventEmitter();
	@Output() ccdRegistered = new EventEmitter<string>();

	readonly State = State;
	currentState = State.Loading;
	terrainRegionFourNineArcSecondId : Number;
	terrainRegionNineEightArcSecondId : Number;
	terrainRegionUpgradableDeviceIdList : Number[];
	requiredUpgradeDevices: Set<Device> = new Set<Device>();
	aircraftDevices: Device[] = [];

	isAircraftLsqCapable: boolean;

	constructor(
		@Inject("terrainRegionFourNineArcSecondId") terrainRegionFourNineArcSecondId: number,
		@Inject("terrainRegionNineEightArcSecondId") terrainRegionNineEightArcSecondId: number,
		@Inject("terrainRegionUpgradableDeviceIdList") terrainRegionUpgradableDeviceIdList: Number[],
		private aircraftService: AircraftService,
		private dealerAircraftService: DealerAircraftService
	) {
		this.terrainRegionFourNineArcSecondId = terrainRegionFourNineArcSecondId;
		this.terrainRegionNineEightArcSecondId = terrainRegionNineEightArcSecondId;
		this.terrainRegionUpgradableDeviceIdList = terrainRegionUpgradableDeviceIdList;
	}

	onCcdRegistered(masterAircraftUuid: string) {
		this.ccdRegistered.emit(masterAircraftUuid)
	}

	onModalClosed() {
		this.modalClosed.emit()
	}

	proceedToActivate() {
		this.currentState = State.MakeLsqCapableSelection;
	}

	onAircraftLsqCapableSelectionMade(isAircraftLsqCapable: boolean){
		this.isAircraftLsqCapable = isAircraftLsqCapable;
		this.currentState = State.ActivateCCD;
	}

	/*
	 * it's important to get the most up-to-date devices from an api call rather than
	 * relying on the devices that got stored when the parent component was loaded.
	 * this is especially important for the terrain synchronization step
	*/
	ngOnInit() {
			this.getAircraft$().subscribe(aircraft => {
				this.aircraftDevices = aircraft.devices;
				if (this.shouldShowSynchronizeTerrain()) {
					this.currentState = State.SynchronizeTerrain;
				} else {
					this.currentState = State.MakeLsqCapableSelection;
				}
			});
	}

	private getAircraft$():Observable<Aircraft> {
		return this.isDealerCustomerManagementFlow ?
			this.dealerAircraftService.getAircraftByUuid(this.aircraftUuid, this.customerGuid, true) :
			this.aircraftService.getOneAircraft(this.aircraftTailNumber, true);
	}

	private shouldShowSynchronizeTerrain(): boolean {
		// get upgradable devices from terrainRegionUpgradableDeviceIdList property
		const upgradableDevices = this.aircraftDevices.filter(device => this.terrainRegionUpgradableDeviceIdList.includes(device.modelID));

		// get device ids that have a terrain db that matches the terrainRegionNineEightArcSecondId property
		upgradableDevices.forEach(device => {
			let hasNineEightArcSecondId: boolean  = false;
			let hasFourNineArcSecondId: boolean = false;
			device.avdbTypes.forEach(avdbType => {
				avdbType.series.forEach(deviceAvdbSeries => {
					if (deviceAvdbSeries.region != null && deviceAvdbSeries.region.id != null) {
						if (deviceAvdbSeries.region.id == this.terrainRegionNineEightArcSecondId && deviceAvdbSeries.installableIssues.length > 0) {
							hasNineEightArcSecondId = true;
						}

						if (deviceAvdbSeries.region.id == this.terrainRegionFourNineArcSecondId && deviceAvdbSeries.installableIssues.length > 0) {
							hasFourNineArcSecondId = true;
						}
					}
				})
			})

			// add to requiredUpgradeDevices only if the device has a 9.8as terrain db, not if the device has a 9.8as and a 4.9as
			if (hasNineEightArcSecondId && !hasFourNineArcSecondId) {
				this.requiredUpgradeDevices.add(device);
			}
		});

		return this.requiredUpgradeDevices.size > 0;
	}
}
